package pl.mothdigital.neuronowi.components.sections.home.hero

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.graphics.*
import com.varabyte.kobweb.silk.style.*
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.keywords.*
import org.jetbrains.compose.web.dom.*
import pl.mothdigital.neuronowi.common.model.ImageResource
import pl.mothdigital.neuronowi.common.resources.ImageResources
import pl.mothdigital.neuronowi.common.resources.StringResources.HERO_SECTION_H1
import pl.mothdigital.neuronowi.common.resources.StringResources.HERO_SECTION_H2
import pl.mothdigital.neuronowi.common.resources.StringResources.HERO_SECTION_P
import pl.mothdigital.neuronowi.common.utils.aspectRatio
import pl.mothdigital.neuronowi.common.utils.loading
import pl.mothdigital.neuronowi.common.utils.fixPolishOrphans
import pl.mothdigital.neuronowi.components.text.*
import pl.mothdigital.neuronowi.components.widgets.*
import pl.mothdigital.neuronowi.theme.*

val HeroBalancedStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(topBottom = 4.cssRem, leftRight = 8.percent)
            .styleModifier {
                property("background-image", "linear-gradient(135deg, ${SiteTheme.green._100}, ${SiteTheme.green._50})")
            }
    }
}

@Composable
fun HeroSection(
    title: String = HERO_SECTION_H1,
    description: String = HERO_SECTION_H2,
    p: String = HERO_SECTION_P,
    image: ImageResource = ImageResources.homeHeroSectionImage,
    modifier: Modifier = Modifier,
) {
    Section(
        attrs = HeroBalancedStyle
            .toModifier()
            .then(modifier)
            .toAttrs()
    ) {
        // Large screen
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .flexWrap(FlexWrap.WrapReverse)
                .alignItems(AlignItems.Center)
                .displayIfAtLeast(Breakpoint.MD),
        ) {
            Column(
                modifier = Modifier
                    .flex(1)
                    .minWidth(450.px)
                    .padding(1.cssRem),
            ) {
                AppH1(color = SiteTheme.green._900) { Text(title.fixPolishOrphans()) }
                VerticalSpacer(12.px)
                AppH2(color = SiteTheme.green._800) { Text(description.fixPolishOrphans()) }
                VerticalSpacer(12.px)
                AppP(color = SiteTheme.gray._800) { Text(p.fixPolishOrphans()) }
            }

            Column(
                modifier = Modifier
                    .flex(1)
                    .minWidth(450.px)
                    .padding(1.cssRem),
            ) {
                Image(
                    src = image.src,
                    alt = image.description,
                    modifier = Modifier
                        .maxWidth(100.percent)
                        .height(auto)
                        .thenIf(image.aspectRatio != null, Modifier.aspectRatio(image.aspectRatio!!))
                        .loading("eager")
                        .borderRadius(1.cssRem)
                        .boxShadow(blurRadius = 12.px, color = rgba(0, 0, 0, 0.07))
                )
            }
        }

        // Small screen
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .alignItems(AlignItems.Center)
                .displayUntil(Breakpoint.MD)
        ) {
            Column(
                modifier = Modifier
                    .flex(1)
                    .padding(1.cssRem),
            ) {
                Image(
                    src = image.src,
                    alt = image.description,
                    modifier = Modifier
                        .maxWidth(100.percent)
                        .height(auto)
                        .thenIf(image.aspectRatio != null, Modifier.aspectRatio(image.aspectRatio!!))
                        .loading("eager")
                        .borderRadius(1.cssRem)
                        .boxShadow(blurRadius = 12.px, color = rgba(0, 0, 0, 0.07))
                )
            }
            Column(
                modifier = Modifier
                    .flex(1)
                    .padding(1.cssRem),
            ) {
                AppH1(color = SiteTheme.green._900) { Text(title.fixPolishOrphans()) }
                VerticalSpacer(12.px)
                AppH2(color = SiteTheme.green._800) { Text(description.fixPolishOrphans()) }
                VerticalSpacer(12.px)
                AppP(color = SiteTheme.gray._800) { Text(p.fixPolishOrphans()) }
            }
        }
    }
}
